import React, { createContext, useContext, useEffect, useState } from "react";
import { eventDispatcher } from "src/helpers/eventEmitter";
import ReactGA from "react-ga4";

type Context = {
  sendEvent: (action: string, data?: object) => void;
  sendCustomEvent: (
    category: string,
    action: string,
    label?: string,
    data?: object
  ) => void;
};

type Props = {
  gaKey: string;
  category?: string;
  trackPages?: boolean;
  children?: React.ReactNode;
  eventParams?: object;
};

export const GoogleAnalyticsContext = createContext<Context>({
  sendEvent: () => {},
  sendCustomEvent: () => {},
});

export const useGoogleAnalytics = () => useContext(GoogleAnalyticsContext);

export const GoogleAnalyticsProvider: React.FC<Props> = ({
  children,
  gaKey,
  category = "",
  trackPages = false,
  eventParams = {},
}) => {
  const [initiated, setInitiated] = useState(false);

  useEffect(() => {
    if (gaKey) {
      ReactGA.initialize(gaKey);
      setInitiated(true);

      const handleEvent = ({
        action,
        payload,
      }: {
        action: string;
        payload?: object;
      }) => {
        let mergedData = { ...eventParams, ...payload, category: category };
        ReactGA.event(action, mergedData);
      };

      eventDispatcher.subscribe(handleEvent);

      return () => {
        eventDispatcher.unsubscribe(handleEvent);
      };
    } else {
      console.warn("Google Analytics key is missing, so it will be disabled");
    }
  }, [gaKey, category, eventParams]);

  if (!initiated) {
    return null;
  }

  const contextValue = {
    sendEvent: (action: string, data = {}): void => {
      eventDispatcher.publish({ action, payload: data });
    },
    sendCustomEvent: (
      customCategory: string,
      action: string,
      label = "",
      data = {}
    ): void => {
      eventDispatcher.publish({
        action,
        payload: { category: customCategory || category, label, ...data },
      });
    },
  };

  return (
    <GoogleAnalyticsContext.Provider value={contextValue}>
      {children}
    </GoogleAnalyticsContext.Provider>
  );
};

export default GoogleAnalyticsProvider;
